import { z } from 'zod';

import { zodAddressSchema } from '@vestigas/data';

import { EmailSchema } from '@vestigas/utils';

import {
  ACTIVATION_FEATURE_TYPE,
  ACTIVATION_REQUEST_STATE,
  CONTACT_LINK_STATUS,
  SUPPLIER_CONTACT_ROLE,
  VISIBILITY_SCOPE,
  SUPPLIER_ACTIVATION_STATUS,
  VOLUME_BUCKET,
  GLOBAL_SUPPLIER_STATUS,
  RELATION_ACTIVATION_STATUS,
  SUPPLIER_HISTORY_ACTION,
} from './constants';

/**
 * Zod schema for per-channel boolean flags (PerChannelFlags).
 *
 * Reused for `enabled_modules` (which channels the customer's account has) and
 * `can_request_activation` (which channels accept a new activation request right now).
 */
export const zodPerChannelFlagsSchema = z.object({
  dln: z.boolean(),
  invoice: z.boolean(),
});

/**
 * Zod schema for a supplier reference (id, name, logo, address), used across
 * multiple supplier endpoints.
 */
export const zodSupplierObjectSchema = z.object({
  address: zodAddressSchema.nullish(),
  id: z.uuid(),
  logoUrl: z.url().nullish(),
  name: z.string(),
});

// ─── Supplier request schemas ─────────────────────────────────────────────────
// Shared between the create-request mutation (POST /suppliers/requests) and
// the request list query (GET /suppliers/requests), which both read/write the
// same SupplierRequest resource shape.

/**
 * Zod schema for a lightweight company reference (id, name) — CompanyRef /
 * CompanyAccountRef.
 */
export const zodCompanyRefSchema = z.object({
  id: z.uuid(),
  name: z.string(),
});

/**
 * Zod schema for the company a supplier request was resolved to
 * (ResolvedCompanyRef).
 */
export const zodResolvedCompanyRefSchema = zodCompanyRefSchema.extend({
  companyAccount: zodCompanyRefSchema,
});

/**
 * Zod schema for a pointer to the caller's most recent rejected request for
 * the same identifier (PriorRejectionRef).
 */
export const zodPriorRejectionRefSchema = z.object({
  rejectedAt: z.iso.datetime({ offset: true }).nullish(),
  rejectionReason: z.string().nullish(),
  requestId: z.uuid(),
  submittedSupplierName: z.string().nullish(),
});

/**
 * Zod schema for the supplier information submitted on a supplier request
 * (SubmittedSupplierInfo).
 */
export const zodSubmittedSupplierInfoSchema = z.object({
  address: zodAddressSchema.nullish(),
  contactEmail: z.string().nullish(),
  dlnCountRange: z.enum(VOLUME_BUCKET).nullish(),
  gln: z.string().nullish(),
  hra: z.string().nullish(),
  hraRegisterCourt: z.string().nullish(),
  hrb: z.string().nullish(),
  invCountRange: z.enum(VOLUME_BUCKET).nullish(),
  note: z.string().nullish(),
  registerCourt: z.string().nullish(),
  revenue: z.string().nullish(),
  supplierName: z.string(),
  vatId: z.string().nullish(),
});

/**
 * Zod schema for the shared supplier activation payload (ActivationCreateBody).
 *
 * Used by both POST /suppliers/{de_id}/activation and
 * POST /suppliers/{de_id}/activation/plan, which accept identical bodies.
 */
export const zodSupplierActivationPayloadBaseSchema = z.object({
  autoFollowUp: z.boolean().default(true),
  customerNumbers: z.array(z.string()).max(500).nullish(),
  dlnVolumeBucket: z.enum(VOLUME_BUCKET).nullish(),
  documentTypes: z.array(z.enum(ACTIVATION_FEATURE_TYPE)).min(1).max(2),
  invoiceVolumeBucket: z.enum(VOLUME_BUCKET).nullish(),
  notifyOnResponse: z.boolean().default(true),
  revenue: z.union([z.number(), z.string()]).nullish(),
  revenueCurrency: z.string().nullish(),
  supplierNote: z.string().nullish(),
});

/**
 * Zod schema for a single role entry in a POST/PATCH contact request payload
 * (SupplierContactRoleInput).
 *
 * Shared between the create and update mutations — both endpoints accept the
 * same person-shaped body with a full role set.
 */
const zodSupplierContactRoleInputSchema = z.object({
  isPrimaryContact: z.boolean().default(false),
  role: z.enum(SUPPLIER_CONTACT_ROLE),
  visibilityScope: z.enum(VISIBILITY_SCOPE).default('company_account'),
});

/**
 * Zod schema for the shared POST/PATCH /suppliers/{de_id}/contacts[/{id}]
 * request payload (SupplierContactCreate / SupplierContactUpdate).
 *
 * Both endpoints accept an identical person-shaped body. The backend creates
 * one DB row per role on POST and reconciles the role set on PATCH, both
 * returning the grouped SupplierContactPerson.
 */
export const zodUpsertSupplierContactPayloadSchema = z.object({
  email: EmailSchema,
  firstName: z.string().trim(),
  lastName: z.string().trim(),
  note: z.string().trim().max(255).nullish(),
  phoneNumber: z.string().trim().nullish(),
  roles: z.array(zodSupplierContactRoleInputSchema).min(1),
});

/**
 * Zod schema for a single role entry on a grouped supplier contact person
 * (SupplierContactRoleEntry — as returned in GET and POST/PATCH responses).
 */
export const zodSupplierContactRoleEntrySchema = z.object({
  hasOptedOut: z.boolean().prefault(false),
  isPrimaryContact: z.boolean().prefault(false),
  role: z.string(),
  visibilityScope: z.enum(VISIBILITY_SCOPE).prefault('company_account'),
});

/**
 * Zod schema for a supplier contact as returned in GET /suppliers/{de_id}
 * and as the response of POST/PATCH contact endpoints (SupplierContactPerson).
 *
 * One entry per person; a person may hold multiple roles via the `roles` array.
 *
 * `email` is a plain string rather than `EmailSchema`: the backend keeps it as
 * stored so a malformed-but-email-shaped legacy value still reaches the client.
 */
export const zodSupplierContactSchema = z.object({
  email: z.string().nullish(),
  firstName: z.string(),
  id: z.uuid(),
  lastName: z.string(),
  note: z.string().nullish(),
  phoneNumber: z.string().nullish(),
  roles: z.array(zodSupplierContactRoleEntrySchema).prefault([]),
});

/**
 * Zod schema for the response returned by POST/PATCH contact endpoints
 * (CreatedUpdatedResponse).
 */
// fallow-ignore-next-line unused-export
export const zodCreatedUpdatedResponseSchema = z.object({
  createdAt: z.string(),
  id: z.uuid(),
  updatedAt: z.string(),
});

// ─── Supplier detail sub-schemas ────────────────────────────────────────────

export const zodSupplierMasterDataSchema = z.object({
  address: zodAddressSchema.nullish(),
  company: zodSupplierObjectSchema,
  gln: z.string().nullish(),
  hra: z.string().nullish(),
  hraRegisterCourt: z.string().nullish(),
  hrb: z.string().nullish(),
  registerCourt: z.string().nullish(),
  ustIdNr: z.string().nullish(),
});

// fallow-ignore-next-line unused-export
export const zodSupplierIntegrationDataSchema = z.object({
  activationStatusDln: z.enum(RELATION_ACTIVATION_STATUS).nullable(),
  activationStatusInvoice: z.enum(RELATION_ACTIVATION_STATUS).nullable(),
  furtherInformation: z.string().trim().nullable(),
  knownIssues: z.string().trim().nullable(),
  notFeasibleReason: z.string().trim().nullable(),
  vestigasNote: z.string().trim().nullable(),
});

/**
 * Zod schema for the responsible person assigned to a supplier relationship
 * (ResponsiblePerson), returned as a full object instead of just an id.
 */
export const zodSupplierResponsiblePersonSchema = z.object({
  email: EmailSchema.nullish(),
  firstName: z.string(),
  id: z.uuid(),
  lastName: z.string(),
  phoneNumber: z.string().nullish(),
});

/**
 * Zod schema for one owner entry in `responsiblePersons`
 * (SupplierResponsiblePersonEntry) -- the base person plus the scope its own
 * assignment was given and the relationship it was assigned on.
 */
export const zodSupplierResponsiblePersonEntrySchema =
  zodSupplierResponsiblePersonSchema.extend({
    // The relationship the assignment was made on. Differs from the current
    // de_id when this owner was carried over from a company_account-scoped
    // assignment on another relationship with the same supplier -- only the
    // owning relationship can re-scope or remove it; others get 404.
    dataExchangeId: z.uuid().nullish(),
    visibilityScope: z.enum(VISIBILITY_SCOPE).prefault('company_account'),
  });

export const zodSupplierRelationshipDataSchema = z.object({
  customerNumbers: z.array(z.string()),
  dlnVolumeBucket: z.enum(VOLUME_BUCKET).nullish(),
  invoiceVolumeBucket: z.enum(VOLUME_BUCKET).nullish(),
  responsiblePerson: zodSupplierResponsiblePersonSchema.nullish(),
  // Every owner of the relationship, earliest assignment first. Managed
  // through /suppliers/{de_id}/responsible_persons; `responsiblePerson`
  // mirrors the first entry and stays populated for one release.
  responsiblePersons: z
    .array(zodSupplierResponsiblePersonEntrySchema)
    .default([]),
  revenue: z.string().nullish(),
  revenueCurrency: z.string().nullish().prefault('EUR'),
  supplierNumber: z.string().nullish(),
});

/**
 * Zod schema for a single contact attempt as returned in the supplier detail response.
 */
// fallow-ignore-next-line unused-export
export const zodContactAttemptSchema = z.object({
  channel: z.string().nullish(),
  createdBy: z
    .object({
      id: z.uuid(),
      name: z.string(),
    })
    .nullish(),
  id: z.uuid(),
  notes: z.string().nullish(),
  result: z.string().nullish(),
  timestamp: z.string(),
  visibilityScope: z.enum(VISIBILITY_SCOPE).prefault('company_account'),
});

/**
 * Zod schema for the supplier detail response (SupplierDetailResponse).
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Suppliers/operation/get_supplier_detail_suppliers_details__de_id__get
 */
export const zodSupplierDetailResponseSchema = z.object({
  activationStatus: z.enum(SUPPLIER_ACTIVATION_STATUS),
  canPlanActivation: z.boolean(),
  canRequestActivation: zodPerChannelFlagsSchema,
  contacts: z.array(zodSupplierContactSchema).nullish(),
  enabledModules: zodPerChannelFlagsSchema,
  globalStatus: z.enum(GLOBAL_SUPPLIER_STATUS).nullish(),
  integration: zodSupplierIntegrationDataSchema,
  masterData: zodSupplierMasterDataSchema,
  recentContactAttempts: z.array(zodContactAttemptSchema).nullish(),
  relationship: zodSupplierRelationshipDataSchema,
});

// ─── Supplier history schemas ────────────────────────────────────────────────

// fallow-ignore-next-line unused-export
export const zodSupplierHistoryFieldChangeSchema = z.object({
  new: z.union([z.string(), z.number(), z.boolean()]).nullish(),
  old: z.union([z.string(), z.number(), z.boolean()]).nullish(),
});

const zodSupplierHistoryContactSchema = z.object({
  email: EmailSchema.nullable(),
  name: z.string().nullable(),
});

/**
 * Zod schema for a single entry in the supplier history response (HistoryEntryResponse).
 */
export const zodSupplierHistoryEntrySchema = z.object({
  action: z.enum(SUPPLIER_HISTORY_ACTION),
  // Plain string rather than EmailSchema: the backend keeps it as stored so a
  // malformed-but-email-shaped legacy value still reaches the client.
  actorEmail: z.string().nullish(),
  actorIpAddress: z.string().nullish(),
  actorName: z.string().nullish(),
  actorType: z.enum(['sender', 'receiver', 'system']),
  changedFields: z
    .record(z.string(), zodSupplierHistoryFieldChangeSchema)
    .nullish(),
  contacts: z.array(zodSupplierHistoryContactSchema),
  documentType: z.enum(ACTIVATION_FEATURE_TYPE).nullable(),
  notes: z.string().nullish(),
  timestamp: z.string(),
});

/**
 * Zod schema for the paginated supplier history response (SupplierHistoryResponse).
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Suppliers/operation/get_supplier_history_suppliers_details__de_id__history_get
 */
export const zodSupplierHistoryResponseSchema = z.object({
  data: z.array(zodSupplierHistoryEntrySchema),
  hasNextPage: z.boolean(),
  limit: z.number().int().gte(1),
  offset: z.number().int().nonnegative().prefault(0),
  paginatedCount: z.number().int().nonnegative(),
  totalCount: z.number().int().nonnegative(),
});

// ─── Activation request ──────────────────────────────────────────────────────

/**
 * Zod schema for an activation request resource returned by the API.
 *
 * Used by GET /suppliers/{req_id}/activation and as the 201 response
 * for POST /suppliers/{de_id}/activation and /activation/plan.
 */
export const zodActivationRequestResponseSchema = z.object({
  autoFollowUp: z.boolean(),
  autoFollowUpAttemptsRemaining: z.number().int().min(0).max(5),
  canceledOn: z.iso.datetime({ offset: true }).nullable(),
  confirmedOn: z.iso.datetime({ offset: true }).nullable(),
  createdOn: z.iso.datetime({ offset: true }),
  customerNumbers: z.array(z.string()).default([]),
  declinedOn: z.iso.datetime({ offset: true }).nullable(),
  declineReason: z.string().nullish(),
  documentType: z.enum(ACTIVATION_FEATURE_TYPE),
  expiredOn: z.iso.datetime({ offset: true }).nullable(),
  id: z.uuid(),
  latestEmailError: z.string().nullable(),
  magicLinkExpiresOn: z.iso.datetime({ offset: true }).nullable(),
  // Plain string rather than EmailSchema: the backend keeps it as stored so a
  // malformed-but-email-shaped legacy value still reaches the client.
  magicLinkSupplierEmail: z.string().nullable(),
  notifyOnResponse: z.boolean(),
  requestedOn: z.iso.datetime({ offset: true }).nullable(),
  state: z.enum(ACTIVATION_REQUEST_STATE),
  supplier: zodSupplierObjectSchema,
  supplierNote: z.string().nullable(),
  versionsUrl: z.string(),
});

// ─── Activation ask response (POST /activation and /activation/plan) ─────────

/**
 * Zod schema for a single contact batch in the POST activation response
 * (BatchActivationStatusResponse).
 *
 * One entry per supplier contact email the backend resolved; single-contact
 * mode returns exactly one entry.
 */
export const zodBatchActivationStatusResponseSchema = z.object({
  activationBatchId: z.uuid().nullable(),
  requests: z.array(zodActivationRequestResponseSchema),
  // Plain string rather than EmailSchema: the backend keeps it as stored so a
  // malformed-but-email-shaped legacy value still reaches the client.
  supplierEmail: z.string().nullable(),
});

/**
 * Zod schema for the POST /suppliers/{de_id}/activation (and /plan) response
 * (ActivationAskResponse).
 *
 * A single ask can produce one batch (single-contact mode) or N batches
 * (multi-contact mode, one per resolved supplier contact).
 */
export const zodActivationAskResponseSchema = z.object({
  batches: z.array(zodBatchActivationStatusResponseSchema),
});

// ─── Contact link ─────────────────────────────────────────────────────────────

/**
 * Zod schema for a contact link resource as returned by the contact_link endpoints
 * (ContactLink).
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Data-Exchange/operation/add_contact_link_data_exchange_contact_link_post
 */
export const zodContactLinkSchema = z.object({
  dataExchangeId: z.uuid(),
  id: z.uuid(),
  lastAccess: z.iso.datetime({ offset: true }).nullable(),
  link: z.string().min(24),
  status: z.enum(CONTACT_LINK_STATUS),
  stillValidFor: z.number().int().nonnegative().nullable(),
  validFor: z.number().int().nonnegative().prefault(96),
});
